import * as React from "react";
import { useParams } from "react-router-dom";
import { ExternalLink } from "react-feather";
import { ChartGroup } from "./ChartGroup";
import { getLocalToken } from "../../services/cpln";
import { METRICS_TOKEN_UPDATE_INTERVAL_MS } from "../../envVariables";
import { getMetricsUrl } from "../../services/utils";
import { useAppSelector } from "../../store/hooks";
import { selectIsAdmin } from "../../store/userSlice";
import { MetricsFilters, MetricsFiltersState, getDefaultFiltersState } from "./MetricsFilters";
import { calculateStep, getTimeRange, CHART_MIN_WIDTH, CHART_MAX_WIDTH, CHARTS_PER_ROW } from "./utils";
// Request Data
import {
  RequestLatencyChart,
  RequestErrorsChart,
  RequestThroughputChart,
  CancelledRequestsChart,
} from "./charts/request";
// CPU
import { CpuUsedChart, CpuPercentUsedChart, CpuReservedChart, CpuBillableChart } from "./charts/cpu";
// Memory
import { MemoryUsedChart, MemoryPercentUsedChart, MemoryReservedChart, MemoryBillableChart } from "./charts/memory";
// General
import {
  ReplicaCountChart,
  ReadyReplicaCountChart,
  ContainerRestartsChart,
  EgressChart,
  DedicatedLoadBalancersChart,
  ProcessingDeploymentsChart,
  DomainWarningsChart,
  WorkloadProgressFailuresChart,
  WorkloadRescheduledReplicasChart,
  CronWorkloadStatusChart,
} from "./charts/general";
// Storage
import { VolumesChart } from "./charts/storage";
// Observability
import { LogsChart, TracesChart } from "./charts/observability";
// Threat Detection
import { ThreatsChart, ThreatsForwardedChart } from "./charts/threatDetection";
// Agents
import { AgentNetworkChart, AgentPeeringChart } from "./charts/agents";

type RouteParams = "org";

export interface MetricsProps {
  gvc?: string[];
  workload?: string[];
  workloadType?: string;
}

const GRID_GAP = 16;

export const Metrics: React.FC<MetricsProps> = ({ gvc: lockedGvc, workload: lockedWorkload, workloadType }) => {
  const { org } = useParams<RouteParams>();
  const [filters, setFilters] = React.useState<MetricsFiltersState>(getDefaultFiltersState());
  const grafanaEndpoint = useAppSelector((state) => state.discovery.endpoints.grafana);
  const isAdmin = useAppSelector(selectIsAdmin);
  const gvc = useAppSelector((state) => state.context.gvc);

  // Measure container width to compute chart pixel width (like Grafana's maxDataPoints)
  const chartsContainerRef = React.useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = React.useState(0);

  React.useEffect(() => {
    const el = chartsContainerRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      const width = entries[0].contentRect.width;
      setContainerWidth(width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Compute per-chart pixel width from the CSS grid layout
  const chartPixelWidth = React.useMemo(() => {
    if (containerWidth === 0) return CHART_MAX_WIDTH; // fallback before first measurement
    const columns = Math.min(CHARTS_PER_ROW, Math.max(1, Math.floor(containerWidth / CHART_MIN_WIDTH)));
    return Math.floor((containerWidth - (columns - 1) * GRID_GAP) / columns);
  }, [containerWidth]);

  // Auto-calculate step from time range and chart width (replicates Grafana's intervalMs logic)
  const step = React.useMemo(() => {
    const { start, end } = getTimeRange(filters.fromIso, filters.toIso);
    const rangeMs = end.getTime() - start.getTime();
    return calculateStep(rangeMs, chartPixelWidth);
  }, [filters.fromIso, filters.toIso, chartPixelWidth]);

  // Grafana link functionality
  const [metricsTokenFromLocal, setMetricsTokenFromLocal] = React.useState("");
  const formRef = React.useRef<HTMLFormElement>(null);

  const onGrafanaLinkClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isAdmin) {
      const workloadName = lockedWorkload && lockedWorkload.length > 0 ? lockedWorkload[0] : "$__all";
      const gvcParam = gvc ? `&var-gvc=${gvc}` : "";
      const adminMetricsUrl = `https://controlplane.grafana.net/d/c9a77afb-a9cc-4f85-b58e-b75fe197376d/metrics-overview-cloud-prod?from=now-6h&to=now&timezone=browser&var-org=${org}${gvcParam}&var-workload=${workloadName}&var-location=$__all&var-percentile=90&var-grouping=workload&var-grouping=gvc`;
      window.open(adminMetricsUrl, "_blank");
      return;
    }
    if (formRef.current) {
      formRef.current.submit();
    }
  };

  React.useEffect(() => {
    getLocalToken().then((res) => {
      if (res.accessToken !== "pass" && formRef.current) {
        setMetricsTokenFromLocal(res.accessToken);
      }
    });
    const localTokenInterval = setInterval(() => {
      getLocalToken().then((res) => {
        if (res.accessToken !== "pass" && formRef.current) {
          setMetricsTokenFromLocal(res.accessToken);
        }
      });
    }, METRICS_TOKEN_UPDATE_INTERVAL_MS);
    return () => {
      clearInterval(localTokenInterval);
    };
  }, []);

  // Memoize array values to prevent unnecessary re-renders in child charts
  // Arrays are compared by reference in useEffect dependencies, so we need stable references
  const effectiveGvc = React.useMemo(
    () => (lockedGvc && lockedGvc.length > 0 ? lockedGvc : filters.gvc),
    [JSON.stringify(lockedGvc), JSON.stringify(filters.gvc)]
  );
  const effectiveWorkload = React.useMemo(
    () => (lockedWorkload && lockedWorkload.length > 0 ? lockedWorkload : filters.workload),
    [JSON.stringify(lockedWorkload), JSON.stringify(filters.workload)]
  );
  const location = React.useMemo(() => filters.location, [JSON.stringify(filters.location)]);
  const container = React.useMemo(() => filters.container, [JSON.stringify(filters.container)]);

  // Build effective grouping: always include locked gvc/workload in requests
  const grouping = React.useMemo(() => {
    const result = [...filters.grouping];
    const isGvcLocked = lockedGvc && lockedGvc.length > 0;
    const isWorkloadLocked = lockedWorkload && lockedWorkload.length > 0;

    // Add locked values to grouping if not already present
    if (isGvcLocked && !result.includes("gvc")) {
      result.unshift("gvc");
    }
    if (isWorkloadLocked && !result.includes("workload")) {
      // Insert after gvc if present, otherwise at start
      const gvcIndex = result.indexOf("gvc");
      if (gvcIndex >= 0) {
        result.splice(gvcIndex + 1, 0, "workload");
      } else {
        result.unshift("workload");
      }
    }
    return result;
  }, [JSON.stringify(filters.grouping), JSON.stringify(lockedGvc), JSON.stringify(lockedWorkload)]);

  // Hide locked GVC from labels (but keep workload visible)
  const hideFromLabel = React.useMemo(() => {
    return lockedGvc && lockedGvc.length > 0 ? ["gvc"] : [];
  }, [JSON.stringify(lockedGvc)]);

  // Conditional flags for hiding chart groups
  const isGvcLocked = lockedGvc && lockedGvc.length > 0;
  const isWorkloadLocked = lockedWorkload && lockedWorkload.length > 0;
  const isGvcOrWorkloadLocked = isGvcLocked || isWorkloadLocked;
  const showCronChart = !workloadType || workloadType === "cron";

  if (!org) {
    return <div className="p-4">No organization specified</div>;
  }

  return (
    <div className="p-4">
      <form
        className="hidden"
        ref={formRef}
        action={`${grafanaEndpoint.replace("{org}", org!)}/auth/process`}
        method="POST"
        target="_blank"
      >
        <input name="redirect" value={
          lockedWorkload && lockedWorkload.length > 0
            ? getMetricsUrl("workload", { workloadName: lockedWorkload[0], gvcName: lockedGvc?.[0] })
            : lockedGvc && lockedGvc.length > 0
              ? getMetricsUrl("gvc", { gvcName: lockedGvc[0] })
              : getMetricsUrl("org")
        } type="hidden" />
        <input name="idToken" value={metricsTokenFromLocal} type="hidden" />
        <button type="submit">metrics</button>
      </form>

      <div className="flex items-center gap-4 mb-4">
        <h1 className="text-xl font-semibold">Metrics</h1>
        <a
          href="#"
          onClick={onGrafanaLinkClick}
          className="flex items-center gap-1 text-sm hover:underline"
          style={{ color: "var(--color-link)" }}
        >
          Explore on Grafana
          <ExternalLink size={14} />
        </a>
      </div>
      <MetricsFilters value={filters} onChange={setFilters} lockedGvc={lockedGvc} lockedWorkload={lockedWorkload} />

      <div ref={chartsContainerRef}>
      <ChartGroup title="Summary" defaultOpen={true}>
        {(!workloadType || workloadType !== "cron") && (
          <RequestLatencyChart
            org={org}
            percentile={filters.percentile}
            workload={effectiveWorkload}
            gvc={effectiveGvc}
            location={location}
            container={container}
            grouping={grouping}
            hideFromLabel={hideFromLabel}
            fromIso={filters.fromIso}
            toIso={filters.toIso}
            step={step}
          />
        )}
        <ReplicaCountChart
          org={org}
          workload={effectiveWorkload}
          gvc={effectiveGvc}
          location={location}
          grouping={grouping}
          hideFromLabel={hideFromLabel}
          fromIso={filters.fromIso}
          toIso={filters.toIso}
          step={step}
        />
        <CpuUsedChart
          org={org}
          workload={effectiveWorkload}
          gvc={effectiveGvc}
          location={location}
          container={container}
          grouping={grouping}
          hideFromLabel={hideFromLabel}
          fromIso={filters.fromIso}
          toIso={filters.toIso}
          step={step}
        />
        <MemoryUsedChart
          org={org}
          workload={effectiveWorkload}
          gvc={effectiveGvc}
          location={location}
          container={container}
          grouping={grouping}
          hideFromLabel={hideFromLabel}
          fromIso={filters.fromIso}
          toIso={filters.toIso}
          step={step}
        />
      </ChartGroup>

      {(!workloadType || workloadType !== "cron") && (
        <div className="mt-4">
        <ChartGroup title="Request Data" defaultOpen={false}>
          <RequestLatencyChart
            org={org}
            percentile={filters.percentile}
            workload={effectiveWorkload}
            gvc={effectiveGvc}
            location={location}
            container={container}
            grouping={grouping}
            hideFromLabel={hideFromLabel}
            fromIso={filters.fromIso}
            toIso={filters.toIso}
            step={step}
          />
          <RequestErrorsChart
            org={org}
            workload={effectiveWorkload}
            gvc={effectiveGvc}
            location={location}
            grouping={grouping}
            hideFromLabel={hideFromLabel}
            fromIso={filters.fromIso}
            toIso={filters.toIso}
            step={step}
          />
          <RequestThroughputChart
            org={org}
            workload={effectiveWorkload}
            gvc={effectiveGvc}
            location={location}
            grouping={grouping}
            hideFromLabel={hideFromLabel}
            fromIso={filters.fromIso}
            toIso={filters.toIso}
            step={step}
          />
          <CancelledRequestsChart
            org={org}
            workload={effectiveWorkload}
            gvc={effectiveGvc}
            location={location}
            grouping={grouping}
            hideFromLabel={hideFromLabel}
            fromIso={filters.fromIso}
            toIso={filters.toIso}
            step={step}
          />
        </ChartGroup>
        </div>
      )}

      <div className="mt-4">
        <ChartGroup title="CPU" defaultOpen={false}>
          <CpuUsedChart
            org={org}
            workload={effectiveWorkload}
            gvc={effectiveGvc}
            location={location}
            container={container}
            grouping={grouping}
            hideFromLabel={hideFromLabel}
            fromIso={filters.fromIso}
            toIso={filters.toIso}
            step={step}
          />
          <CpuPercentUsedChart
            org={org}
            workload={effectiveWorkload}
            gvc={effectiveGvc}
            location={location}
            container={container}
            grouping={grouping}
            hideFromLabel={hideFromLabel}
            fromIso={filters.fromIso}
            toIso={filters.toIso}
            step={step}
          />
          <CpuReservedChart
            org={org}
            workload={effectiveWorkload}
            gvc={effectiveGvc}
            location={location}
            container={container}
            grouping={grouping}
            hideFromLabel={hideFromLabel}
            fromIso={filters.fromIso}
            toIso={filters.toIso}
            step={step}
          />
          <CpuBillableChart
            org={org}
            workload={effectiveWorkload}
            gvc={effectiveGvc}
            location={location}
            container={container}
            grouping={grouping}
            hideFromLabel={hideFromLabel}
            fromIso={filters.fromIso}
            toIso={filters.toIso}
            step={step}
          />
        </ChartGroup>
      </div>

      <div className="mt-4">
        <ChartGroup title="Memory" defaultOpen={false}>
          <MemoryUsedChart
            org={org}
            workload={effectiveWorkload}
            gvc={effectiveGvc}
            location={location}
            container={container}
            grouping={grouping}
            hideFromLabel={hideFromLabel}
            fromIso={filters.fromIso}
            toIso={filters.toIso}
            step={step}
          />
          <MemoryPercentUsedChart
            org={org}
            workload={effectiveWorkload}
            gvc={effectiveGvc}
            location={location}
            container={container}
            grouping={grouping}
            hideFromLabel={hideFromLabel}
            fromIso={filters.fromIso}
            toIso={filters.toIso}
            step={step}
          />
          <MemoryReservedChart
            org={org}
            workload={effectiveWorkload}
            gvc={effectiveGvc}
            location={location}
            container={container}
            grouping={grouping}
            hideFromLabel={hideFromLabel}
            fromIso={filters.fromIso}
            toIso={filters.toIso}
            step={step}
          />
          <MemoryBillableChart
            org={org}
            workload={effectiveWorkload}
            gvc={effectiveGvc}
            location={location}
            container={container}
            grouping={grouping}
            hideFromLabel={hideFromLabel}
            fromIso={filters.fromIso}
            toIso={filters.toIso}
            step={step}
          />
        </ChartGroup>
      </div>

      <div className="mt-4">
        <ChartGroup title="General" defaultOpen={false}>
          <ReplicaCountChart
            org={org}
            workload={effectiveWorkload}
            gvc={effectiveGvc}
            location={location}
            grouping={grouping}
            hideFromLabel={hideFromLabel}
            fromIso={filters.fromIso}
            toIso={filters.toIso}
            step={step}
          />
          <ReadyReplicaCountChart
            org={org}
            workload={effectiveWorkload}
            gvc={effectiveGvc}
            location={location}
            grouping={grouping}
            hideFromLabel={hideFromLabel}
            fromIso={filters.fromIso}
            toIso={filters.toIso}
            step={step}
          />
          <ContainerRestartsChart
            org={org}
            workload={effectiveWorkload}
            gvc={effectiveGvc}
            location={location}
            grouping={grouping}
            hideFromLabel={hideFromLabel}
            fromIso={filters.fromIso}
            toIso={filters.toIso}
            step={step}
          />
          <EgressChart
            org={org}
            workload={effectiveWorkload}
            gvc={effectiveGvc}
            location={location}
            grouping={grouping}
            hideFromLabel={hideFromLabel}
            fromIso={filters.fromIso}
            toIso={filters.toIso}
            step={step}
          />
          {!isWorkloadLocked && (
            <DedicatedLoadBalancersChart
              org={org}
              gvc={effectiveGvc}
              location={location}
              grouping={grouping}
              hideFromLabel={hideFromLabel}
              fromIso={filters.fromIso}
              toIso={filters.toIso}
              step={step}
            />
          )}
          <ProcessingDeploymentsChart
            org={org}
            workload={effectiveWorkload}
            gvc={effectiveGvc}
            location={location}
            grouping={grouping}
            hideFromLabel={hideFromLabel}
            fromIso={filters.fromIso}
            toIso={filters.toIso}
            step={step}
          />
          {!isGvcOrWorkloadLocked && (
            <DomainWarningsChart
              org={org}
              location={location}
              grouping={grouping}
              hideFromLabel={hideFromLabel}
              fromIso={filters.fromIso}
              toIso={filters.toIso}
              step={step}
            />
          )}
          <WorkloadProgressFailuresChart
            org={org}
            workload={effectiveWorkload}
            gvc={effectiveGvc}
            location={location}
            grouping={grouping}
            hideFromLabel={hideFromLabel}
            fromIso={filters.fromIso}
            toIso={filters.toIso}
            step={step}
          />
          <WorkloadRescheduledReplicasChart
            org={org}
            workload={effectiveWorkload}
            gvc={effectiveGvc}
            location={location}
            grouping={grouping}
            hideFromLabel={hideFromLabel}
            fromIso={filters.fromIso}
            toIso={filters.toIso}
            step={step}
          />
          {showCronChart && (
            <CronWorkloadStatusChart
              org={org}
              workload={effectiveWorkload}
              gvc={effectiveGvc}
              location={location}
              grouping={grouping}
              hideFromLabel={hideFromLabel}
              fromIso={filters.fromIso}
              toIso={filters.toIso}
              step={step}
            />
          )}
        </ChartGroup>
      </div>

      {!isWorkloadLocked && (
        <div className="mt-4">
          <ChartGroup title="Storage" defaultOpen={false}>
            <VolumesChart
              org={org}
              gvc={effectiveGvc}
              location={location}
              grouping={grouping}
              hideFromLabel={hideFromLabel}
              fromIso={filters.fromIso}
              toIso={filters.toIso}
              step={step}
            />
          </ChartGroup>
        </div>
      )}

      {!isGvcOrWorkloadLocked && (
        <div className="mt-4">
          <ChartGroup title="Observability" defaultOpen={false}>
            <LogsChart org={org} fromIso={filters.fromIso} toIso={filters.toIso} step={step} />
            <TracesChart org={org} fromIso={filters.fromIso} toIso={filters.toIso} step={step} />
          </ChartGroup>
        </div>
      )}

      {!isWorkloadLocked && (
        <div className="mt-4">
          <ChartGroup title="Threat Detection" defaultOpen={false}>
            <ThreatsChart
              org={org}
              workload={effectiveWorkload}
              gvc={effectiveGvc}
              grouping={grouping}
              hideFromLabel={hideFromLabel}
              fromIso={filters.fromIso}
              toIso={filters.toIso}
              step={step}
            />
            <ThreatsForwardedChart
              org={org}
              workload={effectiveWorkload}
              gvc={effectiveGvc}
              grouping={grouping}
              hideFromLabel={hideFromLabel}
              fromIso={filters.fromIso}
              toIso={filters.toIso}
              step={step}
            />
          </ChartGroup>
        </div>
      )}

      {!isGvcOrWorkloadLocked && (
        <div className="mt-4">
          <ChartGroup title="Agents" defaultOpen={false}>
            <AgentNetworkChart
              org={org}
              grouping={grouping}
              hideFromLabel={hideFromLabel}
              fromIso={filters.fromIso}
              toIso={filters.toIso}
              step={step}
            />
            <AgentPeeringChart
              org={org}
              grouping={grouping}
              hideFromLabel={hideFromLabel}
              fromIso={filters.fromIso}
              toIso={filters.toIso}
              step={step}
            />
          </ChartGroup>
        </div>
      )}
      </div>
    </div>
  );
};
